import Observer from 'utils/Observer';
import Toggle from 'toggle/Toggle';
import Trigger from 'toggle/Trigger';
import Request from 'utils/Request';
import conditioner from 'conditioner-js';

/**
 * @module Overlay
 */
class Overlay {
    /**
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - ConditionerJS's merged options
     */
    constructor(element, options) {
        this._element = element;
        this._options = Object.assign(Overlay.options, options);
        this._overlay = null;
        this._wrapper = null;
        this._iframe = null;
        this._content = null;
        this._id = `${this._element.id}_overlay`;
        this.load();
    }


    /**
     * Base options
     */
    static get options() {
        return {
            classnameOverlay: 'overlay',
            classnameWrapper: 'overlay_wrapper',
            classnameContent: 'overlay_content',
            classnameIframe: 'overlay_iframe',
            classnameCloseBtn: 'overlay_closer',
            modal: false,
            contentType: null
        };
    }


    /**
     * Construct module
     */
    load() {
        this._overlay = document.createElement('div');
        this._overlay.id = this._id;
        this._overlay.className = this._options.classnameOverlay;

        this._wrapper = document.createElement('div');
        this._wrapper.className = this._options.classnameWrapper;
        this._overlay.appendChild(this._wrapper);

        if (!this._options.modal) {
            const btnClose = document.createElement('button');
            btnClose.className = this._options.classnameCloseBtn;
            btnClose.textContent = 'X';
            this._wrapper.appendChild(btnClose);
            new Trigger(btnClose, { targets: this._id });
        }

        document.body.appendChild(this._overlay);

        new Trigger(this._element, { targets: this._id });
        const toggle = new Toggle(this._overlay);

        Observer.subscribe(toggle, toggle.activeEvent, () => this._setup());
    }


    /**
     * Setup the loading of content
     * @private
     */
    _setup() {
        switch (this._options.contentType) {
            case 'iframe':
                this._setupIframe();
                break;
            case 'ajax':
                this._setupAjax();
                break;
            case 'internal':
            default:
                this._setupInternal();
        }
    }


    /**
     * Add the iframe content
     * @private
     */
    _setupIframe() {
        if (this._iframe) {
            return;
        }

        const iframe = document.createElement('iframe');
        iframe.className = this._options.classnameIframe;
        iframe.src = this._element.href;
        this._wrapper.appendChild(iframe);

        this._iframe = iframe;
    }

    /**
     * Fetch the ajax content
     * @private
     */
    _setupAjax() {
        Request.fetchPlainText(this._element.href, true).then(response => this._addContent(response), () => this._onRequestRejected());
    }

    /**
     * Add the internal content
     * @private
     */
    _setupInternal() {
        const target = document.getElementById(this._element.href.replace(/^.*?#/, ''));
        this._addContent(target.innerHTML);
    }


    /**
     * error handling
     * @return {boolean} false
     * @private
     */
    _onRequestRejected() {
        return false;
    }


    /**
     * Add the actual content
     * @param {string} content - The content
     * @private
     */
    _addContent(content) {
        if (!this._content) {
            this._content = document.createElement('div');
            this._content.className = this._options.classnameContent;
            this._wrapper.appendChild(this._content);
        }

        this._content.innerHTML = content;
        conditioner.parse(this._content);
    }

}

// Exports
export default Overlay;
